import Component from '@glimmer/component';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import ArrowUpTray from 'scaleflow/components/tailwind-icons/arrow-up-tray';

export interface RfiDropzoneSignature {
  Element: HTMLDivElement;
  Args: {
    Named: {
      /** Category title, used to label the file input for assistive tech. */
      categoryTitle: string;
      onFiles: (files: File[]) => void;
    };
  };
}

export default class RfiDropzone extends Component<RfiDropzoneSignature> {
  @tracked isDragging = false;

  @action
  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  @action
  onDragLeave() {
    this.isDragging = false;
  }

  @action
  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    this.emit(Array.from(event.dataTransfer?.files ?? []));
  }

  @action
  onPick(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    // Reset so picking the same file again after removing it still fires `change`.
    input.value = '';
    this.emit(files);
  }

  private emit(files: File[]) {
    if (files.length) this.args.onFiles(files);
  }

  <template>
    {{! template-lint-disable no-invalid-interactive }}
    <div
      class="flex items-center justify-between gap-3 rounded-lg border-2 border-dashed p-4
        {{if
          this.isDragging
          'border-primary-500 bg-primary-100'
          'border-primary-300 bg-primary-50'
        }}"
      {{on "dragover" this.onDragOver}}
      {{on "dragleave" this.onDragLeave}}
      {{on "drop" this.onDrop}}
      ...attributes
    >
      <span class="flex items-center gap-3 text-sm text-primary-800">
        <ArrowUpTray class="h-5 w-5 shrink-0" />
        {{t "rfi.dropzone"}}
      </span>
      <label
        class="inline-flex min-h-[44px] cursor-pointer items-center rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700 focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2"
      >
        <input
          type="file"
          multiple
          class="sr-only"
          {{on "change" this.onPick}}
        />
        {{t "rfi.add-files"}}<span class="sr-only">: {{@categoryTitle}}</span>
      </label>
    </div>
  </template>
}
